import React, { useState, useMemo, useEffect } from 'react';
import moment from 'moment';
import { useQuery } from 'react-query';
import { AlertBar, IndicatorSmaControls } from '~/shared/components';
import { queryOptions, colors } from '~/shared/constants';
import { fetchIndicatorData } from '~/shared/services/api';
import { simpleMovingAverage, blendSmaWithRawResidual } from '~/shared/utils';
import { resolveColumnNodeDescription, buildColumnDescriptionByName } from '~/shared/utils/columnNodeDescription';
import { DEFAULT_RANGE_SELECTOR_INDEX, getRangeSelectorIndexFromWindow } from '~/shared/utils/timeWindow';
import { useSmaState, DEFAULT_SMA_VALUE } from '../hooks/useSmaState';
import IndicatorLineChartBase from './IndicatorLineChartBase';

import { Root, HighchartContainer, AlignedLoader, ControlsWrapper } from './IndicatorLineChart.styles';
import { ISeriesObj } from './IndicatorLineChart.types';

export default function IndicatorLineChart({
  category,
  prism,
  prismId,
  indicator,
  cellInfo,
  filterValues,
  tooltipCfg,
}: any) {
  const [doneHide, setDoneHide] = useState(false);
  const [chartTitle, setChartTitle] = useState('');
  const [chartSubtitle, setChartSubtitle] = useState('');
  const [chartYtitle, setChartYtitle] = useState('');
  const [error, setError] = useState<any>(null);

  const {
    data: apiData,
    error: dataError,
    isLoading,
  } = useQuery(
    ['fetchIndicatorData', indicator, cellInfo, JSON.stringify(filterValues)],
    async () => {
      const obj = await fetchIndicatorData(category, prism, prismId, indicator, cellInfo, filterValues);
      const colorScheme =
        obj?.color_scheme ||
        (obj &&
          colors
            .slice(-obj.col_nodes.length)
            .reverse()
            .map((color: string) => getComputedStyle(document.documentElement).getPropertyValue(color)));
      const highchartsConfig = obj?.highcharts_config ?? obj?.hicharts_config;
      const rawRangeMode =
        highchartsConfig?.yAxis?.range_mode ??
        highchartsConfig?.yAxis?.rangeMode ??
        highchartsConfig?.yAxis_range_mode ??
        highchartsConfig?.yAxisRangeMode ??
        highchartsConfig?.range_mode ??
        highchartsConfig?.rangeMode ??
        highchartsConfig?.yAxis?.mode ??
        obj?.yAxis?.range_mode ??
        obj?.yAxis?.rangeMode ??
        obj?.yAxis_range_mode ??
        obj?.yAxisRangeMode ??
        obj?.range_mode ??
        obj?.rangeMode ??
        obj?.yAxis?.mode;
      const normalizedRangeMode = typeof rawRangeMode === 'string' ? rawRangeMode.trim().toLowerCase() : '';
      const rawPaddingRatio =
        highchartsConfig?.yAxis?.padding_ratio ??
        highchartsConfig?.yAxis?.paddingRatio ??
        highchartsConfig?.yAxis_padding_ratio ??
        highchartsConfig?.yAxisPaddingRatio ??
        highchartsConfig?.padding_ratio ??
        highchartsConfig?.paddingRatio ??
        obj?.yAxis?.padding_ratio ??
        obj?.yAxis?.paddingRatio ??
        obj?.yAxis_padding_ratio ??
        obj?.yAxisPaddingRatio ??
        obj?.padding_ratio ??
        obj?.paddingRatio;
      const parsedPaddingRatio = Number(rawPaddingRatio);
      const normalizedPaddingRatio =
        Number.isFinite(parsedPaddingRatio) && parsedPaddingRatio > 1 && parsedPaddingRatio <= 100
          ? parsedPaddingRatio / 100
          : parsedPaddingRatio;
      const yAxisPaddingRatio =
        Number.isFinite(normalizedPaddingRatio) && normalizedPaddingRatio >= 0 && normalizedPaddingRatio < 1
          ? normalizedPaddingRatio
          : 0.2;
      const isPaddedMode =
        normalizedRangeMode === '' ||
        normalizedRangeMode === 'padded-visible' ||
        normalizedRangeMode === 'padded_visible' ||
        normalizedRangeMode === 'paddedvisible' ||
        normalizedRangeMode === 'dynamic' ||
        normalizedRangeMode === 'auto';
      const yAxisRangeMode = isPaddedMode ? 'padded-visible' : 'zero-based';
      const numberOfLines = obj?.number_of_lines;
      const xLabels = obj.row_nodes.map((r: any) => r.name);
      const yLabels = obj.col_nodes.map((c: any) => c.name);
      const descriptionByName = buildColumnDescriptionByName(obj.col_nodes, obj.secondary_col_nodes);
      const seriesData: any[] = [];
      for (let j = 0; j < yLabels.length; ++j) {
        const colIndex = yLabels.length - j - 1;
        const columnNode = obj.col_nodes[colIndex];
        const seriesName = yLabels[colIndex];
        const seriesDescription =
          resolveColumnNodeDescription(columnNode, obj.secondary_col_nodes, descriptionByName) ??
          descriptionByName[seriesName];
        const seriesObj: ISeriesObj = {
          name: seriesName,
          data: [],
          showInNavigator: true,
          color: colorScheme ? colorScheme[yLabels.length - j - 1] : undefined,
          ...(typeof seriesDescription === 'string' && seriesDescription.trim()
            ? { custom: { description: seriesDescription } }
            : {}),
        };
        for (let i = 0; i < xLabels.length; ++i) {
          const value: number = obj.data[i][yLabels.length - j - 1] as unknown as number;
          seriesObj.data.push([moment.utc(xLabels[i]).valueOf(), value]);
        }
        seriesObj.title = obj.title || '';
        seriesObj.subtitle = obj.subtitle || `${cellInfo.topic || ''} ${cellInfo.symbol || ''} ${indicator}`;
        seriesObj.ytitle = obj.ytitle || '';
        seriesData.push(seriesObj);
      }
      return {
        seriesData,
        seriesDescriptionByName: descriptionByName,
        highchartsConfig,
        numberOfLines,
        sma: obj.sma,
        defaultTimeWindow: obj?.default_time_window,
        yAxisRangeMode,
        yAxisPaddingRatio,
      };
    },
    queryOptions,
  );

  const { smaOptions, smaValue, setSmaValue, smaEnabled } = useSmaState(apiData?.sma);

  const handleSmaChange = (val: any) => {
    setSmaValue(Number(val));
  };

  const displayedSmaValue = React.useMemo(() => {
    if (!smaOptions.includes(smaValue)) {
      return smaOptions[0] ?? DEFAULT_SMA_VALUE;
    }
    return smaValue;
  }, [smaOptions, smaValue]);

  let exportFileName = `${cellInfo.topic ? `${cellInfo.topic}_` : ''}${
    cellInfo.symbol ? `${cellInfo.symbol}_` : ''
  }${indicator}`;

  Object.keys(filterValues).forEach(function (key) {
    if (key !== 'TimeResolution') exportFileName += `_${filterValues[key]}`;
  });

  const data = useMemo(() => {
    if (!apiData) return;
    const tempData = JSON.parse(JSON.stringify(apiData));
    if (tempData && tempData.seriesData) {
      const smoothWindow = !smaEnabled ? 1 : Number(smaValue);
      const tempSeriesData = tempData.seriesData.map((series: any, index: number) => {
        const baseSeriesSource = apiData?.seriesData?.[index]?.data;
        const baseSeriesData = Array.isArray(baseSeriesSource) ? baseSeriesSource : [];
        const sortedSeriesData = [...baseSeriesData].sort((a: [number, number], b: [number, number]) => a[0] - b[0]);

        if (!Number.isFinite(smoothWindow) || smoothWindow <= 1) {
          return { ...series, data: sortedSeriesData };
        }
        const smaPoints = simpleMovingAverage(sortedSeriesData, smoothWindow);
        const smoothed = blendSmaWithRawResidual(sortedSeriesData, smaPoints, smoothWindow);
        return {
          ...series,
          data: smoothed.length > 0 ? smoothed : sortedSeriesData,
        };
      });
      return { seriesData: tempSeriesData, numberOfLines: apiData?.numberOfLines };
    }
    return null;
  }, [apiData, smaValue, smaEnabled]);

  useEffect(() => {
    if (!data?.seriesData) return;
    const obj = data.seriesData[0];
    setChartTitle(obj.title || '');
    const chartSubtitle = obj.subtitle || `${cellInfo.topic || ''} ${cellInfo.symbol || ''} ${indicator}`;
    setChartSubtitle(chartSubtitle === ' ' ? '' : chartSubtitle);
    setChartYtitle(obj.ytitle || '');
  }, [data]);

  useEffect(() => {
    if (!data?.seriesData) return;
  }, [chartTitle, data]);

  useEffect(() => {
    if (dataError) {
      setError(dataError);
    }
    if (isLoading) {
      setError(null);
    }
  }, [dataError, isLoading]);

  const defaultRangeIndex = useMemo(
    () => getRangeSelectorIndexFromWindow(apiData?.defaultTimeWindow),
    [apiData?.defaultTimeWindow],
  );

  const [selectedRangeSelector, setSelectedRangeSelector] = useState<number>(
    () => defaultRangeIndex ?? DEFAULT_RANGE_SELECTOR_INDEX,
  );
  const [chartTooltipVisible, setChartTooltipVisible] = useState(false);

  useEffect(() => {
    const nextIndex = defaultRangeIndex ?? DEFAULT_RANGE_SELECTOR_INDEX;
    setSelectedRangeSelector(prev => (prev === nextIndex ? prev : nextIndex));
  }, [defaultRangeIndex, apiData]);

  if (isLoading)
    return (
      <Root>
        <HighchartContainer>
          <AlignedLoader />
        </HighchartContainer>
      </Root>
    );

  return (
    <Root>
      {data && !isLoading && smaEnabled && !chartTooltipVisible && (
        <ControlsWrapper>
          <IndicatorSmaControls options={smaOptions} value={displayedSmaValue} onChange={handleSmaChange} />
        </ControlsWrapper>
      )}
      <IndicatorLineChartBase
        data={data?.seriesData}
        apiData={apiData}
        chartTitle={chartTitle}
        chartSubtitle={chartSubtitle}
        chartYtitle={chartYtitle}
        exportFileName={exportFileName}
        prism={prism}
        tooltipCfg={tooltipCfg}
        onTooltipVisibilityChange={setChartTooltipVisible}
        doneHide={doneHide}
        setDoneHide={setDoneHide}
        smaValue={smaValue}
        selectedRangeSelector={selectedRangeSelector}
        setSelectedRangeSelector={setSelectedRangeSelector}
        numberOfLines={data?.numberOfLines}
        yAxisRangeMode={apiData?.yAxisRangeMode}
        yAxisPaddingRatio={apiData?.yAxisPaddingRatio}
        seriesDescriptionByName={apiData?.seriesDescriptionByName}
      />
      {error && <AlertBar message={error.message} />}
    </Root>
  );
}
